import React, { useCallback, useEffect, useRef } from 'react';
import type {
  CatalogInteraction,
  CatalogNotifier,
} from '@components/ui/catalog/ResourceCatalog/ResourceCatalog.interactions';
import { useOrganizationId } from '@features/user';
import { Alert, Button } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useTemplateCatalogData } from '../hooks/useTemplateCatalogData';

import { TemplateCatalog } from './TemplateCatalog';

/** Search filter edits are keystroke-level — debounce before tracking. */
const SEARCH_TRACK_DEBOUNCE_MS = 1500;

/**
 * Live container for the note-template catalog (GDN-2482): bootstrap read +
 * batch archive/de-adopt verbs via useTemplateCatalogData, Statsig tracking on
 * the catalog's onAction/onInteraction seams. The hosting page owns the feature
 * gate, the permission check, and the header.
 */
export const TemplateCatalogContainer: React.FC = () => {
  const organizationId = useOrganizationId() ?? undefined;
  const { templateCatalog } = useEventTracking();
  const { templates, isLoading, isError, refetch, verbs } =
    useTemplateCatalogData(organizationId);

  // One opened event per mount, once the data is actually in front of the
  // admin (counts are the payload — no point firing while loading).
  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current || isLoading || isError || templates.length === 0) {
      return;
    }
    openedRef.current = true;
    templateCatalog.opened({
      organization_id: organizationId,
      total_templates: templates.length,
      archived_count: templates.filter((t) => t.archived).length,
      shared_count: templates.filter((t) => t.owner_scope === 'org').length,
      personal_count: templates.filter((t) => t.owner_scope === 'user').length,
      public_count: templates.filter((t) => t.owner_scope === 'public').length,
    });
  }, [templates, isLoading, isError, organizationId, templateCatalog]);

  const searchTrackTimer = useRef<number | null>(null);
  useEffect(
    () => (): void => {
      if (searchTrackTimer.current !== null) {
        window.clearTimeout(searchTrackTimer.current);
      }
    },
    []
  );

  const handleInteraction = useCallback(
    (interaction: CatalogInteraction): void => {
      switch (interaction.type) {
        case 'filter_changed': {
          const metadata = {
            organization_id: organizationId,
            scope: interaction.filters.scope,
            owners_count: interaction.filters.owners.length,
            has_search: interaction.filters.search.trim() !== '',
            show_archived: interaction.filters.showArchived,
          };
          // Search edits arrive per keystroke — debounce those; fire
          // structural filter changes (scope/owner/archived) immediately.
          if (searchTrackTimer.current !== null) {
            window.clearTimeout(searchTrackTimer.current);
            searchTrackTimer.current = null;
          }
          if (metadata.has_search) {
            searchTrackTimer.current = window.setTimeout(() => {
              templateCatalog.filterChanged(metadata);
            }, SEARCH_TRACK_DEBOUNCE_MS);
          } else {
            templateCatalog.filterChanged(metadata);
          }
          return;
        }
        case 'sort_changed':
          templateCatalog.sortChanged({
            organization_id: organizationId,
            sort_key: interaction.sort.key,
            direction: interaction.sort.direction,
          });
          return;
        case 'row_expanded':
          templateCatalog.rowDetailOpened({
            organization_id: organizationId,
            row_id: interaction.id,
          });
          return;
        case 'row_collapsed':
          // Collapse is not tracked ("_collapsed" isn't an allowlisted suffix,
          // and the open is the signal that matters).
          return;
        case 'undo_clicked':
          templateCatalog.undoClicked({
            organization_id: organizationId,
            action: interaction.action,
            count: interaction.ids.length,
          });
          return;
      }
    },
    [organizationId, templateCatalog]
  );

  const handleAction = useCallback(
    (action: 'archive' | 'restore', ids: string[]): void => {
      templateCatalog.actionSubmitted({
        organization_id: organizationId,
        action,
        count: ids.length,
      });
    },
    [organizationId, templateCatalog]
  );

  // Verb-outcome toasts through the app's notistack (same look as every other
  // notification) instead of the catalog's built-in fallback Snackbar.
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const notifier = useCallback<CatalogNotifier>(
    ({ severity, message, onUndo }) => {
      enqueueSnackbar(message, {
        variant: severity,
        autoHideDuration: 5000,
        action: onUndo
          ? (snackbarKey): React.ReactNode => (
              <Button
                color="inherit"
                size="small"
                onClick={(): void => {
                  closeSnackbar(snackbarKey);
                  onUndo();
                }}
              >
                Undo
              </Button>
            )
          : undefined,
      });
    },
    [enqueueSnackbar, closeSnackbar]
  );

  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        Your note templates could not be loaded.
      </Alert>
    );
  }

  return (
    <TemplateCatalog
      templates={templates}
      verbs={verbs}
      loading={isLoading}
      onAction={handleAction}
      onInteraction={handleInteraction}
      notifier={notifier}
    />
  );
};
